<script setup lang="ts">
import { computed } from 'vue';
import type { Grade } from '@/utils/grade';

const props = withDefaults(defineProps<{
  grade?: Grade | null;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}>(), {
  size: 'md',
});

const tierClass = computed(() => {
  if (!props.grade) return null;
  if (props.grade[0] === 'S') return 'tier-s';
  if (props.grade[0] === 'A') return 'tier-a';
  if (props.grade[0] === 'B') return 'tier-b';
  if (props.grade[0] === 'C') return 'tier-c';
  if (props.grade === 'D') return 'tier-d';
  return 'tier-f';
});

const sizeClass = computed(() => `size-${props.size}`);
</script>

<template>
  <span
    v-if="grade"
    class="badge"
    :class="[tierClass, sizeClass]"
    :aria-label="`Grade ${grade}`"
  >{{ grade[0] }}<span v-if="grade.endsWith('+')" class="plus" aria-hidden="true">+</span></span>
  <span
    v-else
    class="badge-empty"
    :class="sizeClass"
    aria-label="No grade yet"
  >—</span>
</template>

<style scoped>
/*
  Pure-typography rank mark. No background, no radius, no shadow.
  - The letter glyph is the primary differentiator; tier color is
    reinforcement (color-blind safe).
  - F/D/C use the app's auto-flipping text tokens. B/A/S carry per-theme
    accents tuned for AA contrast; S clips an animated gradient through the
    glyph itself.
  - The cell holds a fixed width/height so adjacent table rows stay aligned
    whether the glyph is narrow ("S") or wide ("A+").
*/

.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  vertical-align: middle;
  font-style: italic;
  font-weight: 800;
  letter-spacing: -0.04em;
  line-height: 1;
}

.plus {
  font-style: normal;
  font-weight: 700;
  font-size: 0.62em;
  letter-spacing: 0;
  margin-left: 0.04em;
  align-self: flex-start;
  margin-top: 0.06em;
}

/* Tier color — drawn from the app's semantic palette where possible.
   F/D/C use neutral text tokens that already flip with theme. B/A/S take
   per-theme accents tuned for AA contrast on each surface. */

.tier-f { color: var(--color-text-secondary); opacity: 0.85; }
.tier-d { color: var(--color-text-tertiary); }
.tier-c { color: var(--color-text-primary); }
.tier-b { color: #1d4ed8; }
.tier-a { color: #15803d; }

/* S — the singular celebration. The letter itself becomes the gradient via
   background-clip; per-theme stops keep AA-large contrast on both surfaces. */
.tier-s {
  /*
    Smooth-loop trick: the stops contain TWO full hue cycles, and the
    animation only travels through one cycle's worth (half the tile). The end
    state is visually identical to the start because the gradient's two
    halves are copies, but the visible window stays inside the gradient's
    interior — never crossing the tile edge — so subpixel rasterization at
    the seam can't produce a visible flicker on wraparound.

    Visible badge slice ≈ 33% of the 300%-wide tile = ~4 hue segments at
    once. 90deg keeps the gradient's repeat axis aligned with the animation
    axis. Stops are evenly spaced on the hue wheel for a constant flow rate.
  */
  --tier-s-stops:
    #dc2626, #d97706, #15803d, #0891b2, #2563eb, #a21caf,
    #dc2626, #d97706, #15803d, #0891b2, #2563eb, #a21caf, #dc2626;
  background: linear-gradient(90deg, var(--tier-s-stops));
  background-size: 300% 100%;
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
  animation: badge-rainbow 7s linear infinite;
}

/* Dark theme overrides — the document root carries `.dark` (App.vue), so this
   ancestor selector is stable across the scoped boundary. */
:where(html.dark) .tier-b { color: #93c5fd; }
:where(html.dark) .tier-a { color: #86efac; }
:where(html.dark) .tier-s {
  --tier-s-stops:
    #f87171, #fbbf24, #4ade80, #22d3ee, #60a5fa, #e879f9,
    #f87171, #fbbf24, #4ade80, #22d3ee, #60a5fa, #e879f9, #f87171;
}

@keyframes badge-rainbow {
  /* End at exactly half the tile (= one full hue cycle) so the next
     iteration's `from` is visually identical to this iteration's `to`. */
  from { background-position: 0% 50%;   }
  to   { background-position: 150% 50%; }
}

/* Sizes — cells reserved tight; visible letter does its own work. */
.size-sm { width: 1.375rem; height: 1.125rem; font-size: 0.8rem;   }
.size-md { width: 1.875rem; height: 1.5rem;   font-size: 1.0625rem; }
.size-lg { width: 3rem;     height: 2.375rem; font-size: 2.125rem; }
.size-xl { width: 5rem;     height: 4rem;     font-size: 3.75rem;  }

.badge-empty {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  color: var(--color-text-secondary);
  opacity: 0.35;
  font-weight: 600;
  user-select: none;
}
</style>
